import { useEffect, useState } from 'react';
import React from 'react';
import Icon from '@/components/atoms/icon/icon';
import RichText from '@/components/atoms/rich-text/rich-text';
import SkeletonLoader from '@/components/atoms/skeleton-loader/skeleton-loader';
import Typography from '@/components/atoms/typography/typography';
import { getPaxInfo } from '@/components/modules/configuration-flow/configuration-flow-utils';
import Select, { SelectOption } from '@/components/molecules/select/select';
import useQueryDepartureSelector from '@/integrations/content/content-modules/departure-selector/departure-selector.hooks';
import { useHotelContext } from '@/libraries/contexts/hotel-context/hotel-context';
import { useI18n } from '@/libraries/contexts/i18n-context';
import { usePredefinedSearchContext } from '@/libraries/contexts/predefined-search-context/predefined-search-context';
import { capitalizeFirstLetter } from '@/libraries/formatters/capitalize-first-letter';
import useDateFormatter from '@/libraries/formatters/use-date-formatter/use-date-formatter';
import DepartureSelectorItem from './departure-selector-item/departure-selector-item';
import styles from './departure-selector.module.scss';

const VISIBLE_ITEMS = 3;

type SortKey = 'date' | 'airport' | 'price';
type SortDirection = 'asc' | 'desc';

export interface DepartureSelectorProps {
  /** The UUID of the module */
  id: Content.Components['DepartureSelectorModuleHeadlessDto']['id'];
  /** The UUID of the current page */
  pageId: Content.Components['DepartureSelectorModuleHeadlessDto']['pageId'];
  /** Additional parameters */
  parameters?: Content.Components['DepartureSelectorModuleHeadlessDto']['parameters'];
}

const DepartureSelector = ({
  id,
  pageId,
  parameters,
}: DepartureSelectorProps) => {
  const { t } = useI18n();
  const { setTransportAllotmentId, setOfferId } = usePredefinedSearchContext();
  const { setCurrentStepSummary, searchSettings, setPrice } = useHotelContext();
  const { dateFormatter } = useDateFormatter();
  const [departures, setDepartures] = useState<
    Content.Components['DepartureSelectorModuleDepartureDto'][]
  >([]);
  const [showAll, setShowAll] = useState(false);
  const [sort, setSort] = useState<{ direction: SortDirection; key: SortKey }>({
    direction: 'asc',
    key: 'date',
  });
  const [filterDepartureCode, setFilterDepartureCode] = useState<string>('all');
  const [filterDepartureOptions, setFilterDepartureOptions] = useState<
    SelectOption[]
  >([]);

  const {
    data: dataDepartureSelector,
    isFetching: isFetchingDepartureSelector,
  } = useQueryDepartureSelector({
    parameters: parameters && {
      ...parameters,
      id,
      pageId,
      predefinedSearchId: parameters.predefinedSearchId as number,
    },
  });

  const generateStepSummary = (
    departure: Content.Components['DepartureSelectorModuleDepartureDto'],
  ) => {
    return `${dateFormatter(departure.outboundDepartureDate).format('DD. MMM')} - ${dateFormatter(departure.homeboundDepartureDate).format('DD. MMM YYYY')}; ${departure.outboundDepartureName}`;
  };

  const setDepartureData = (
    departure: Content.Components['DepartureSelectorModuleDepartureDto'],
  ) => {
    setTransportAllotmentId(departure.transportAllotmentId.toString());
    setOfferId(departure.offerId);
    setCurrentStepSummary(generateStepSummary(departure));
    setPrice(departure.price ?? undefined);
  };

  useEffect(() => {
    if (dataDepartureSelector?.departures) {
      setDepartures(dataDepartureSelector.departures);

      const selectedDeparture = dataDepartureSelector.departures.find(
        departure => departure.isSelected,
      );

      if (selectedDeparture) {
        setDepartureData(selectedDeparture);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataDepartureSelector?.departures]);

  const handleDepartureClick = (
    departure: Content.Components['DepartureSelectorModuleDepartureDto'],
  ) => {
    setDepartures(previous =>
      previous.map(item => ({
        ...item,
        isSelected:
          item.transportAllotmentId === departure.transportAllotmentId,
      })),
    );

    setDepartureData(departure);
  };

  // Filter logic
  const filteredDepartures =
    filterDepartureCode === 'all'
      ? departures
      : departures.filter(
          departure => departure.outboundDepartureCode === filterDepartureCode,
        );

  // Sorting logic
  const sortedDepartures = [...filteredDepartures].sort((a, b) => {
    let compare = 0;

    if (sort.key === 'date') {
      compare =
        new Date(a.outboundDepartureDate as string).getTime() -
        new Date(b.outboundDepartureDate as string).getTime();
    } else if (sort.key === 'airport') {
      compare = a.outboundDepartureName.localeCompare(b.outboundDepartureName);
    } else if (sort.key === 'price') {
      compare = (a.price ?? 0) - (b.price ?? 0);
    }

    return sort.direction === 'asc' ? compare : -compare;
  });

  const hasNoResults =
    !isFetchingDepartureSelector && !dataDepartureSelector?.departures.length;

  const handleSortClick = (key: SortKey) => {
    setSort(previous => ({
      direction:
        previous.key === key && previous.direction === 'asc' ? 'desc' : 'asc',
      key,
    }));
  };

  const handleFilterDepartureSelect = (
    event: React.ChangeEvent<HTMLSelectElement>,
  ) => {
    setFilterDepartureCode(event.target.value);
  };

  // Generate filterDepartureOptions based on available departures
  useEffect(() => {
    const options: SelectOption[] = [
      {
        name: capitalizeFirstLetter(t('items.common.words.all')),
        value: 'all',
      },
      ...(dataDepartureSelector?.availableDepartures
        ? dataDepartureSelector.availableDepartures.map(departure => ({
            id: departure.departureCode,
            name: departure.departureName,
            value: departure.departureCode,
          }))
        : []),
    ];

    setFilterDepartureOptions(options);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataDepartureSelector?.availableDepartures]);

  const renderResults = () => {
    if (isFetchingDepartureSelector) {
      return (
        <div className={styles.departuresWrapper}>
          <SkeletonLoader borderRadius="4px" height="85px" width="100%" />

          <SkeletonLoader borderRadius="4px" height="85px" width="100%" />

          <SkeletonLoader borderRadius="4px" height="85px" width="100%" />
        </div>
      );
    }

    const visibleDepartures = showAll
      ? sortedDepartures
      : sortedDepartures.slice(0, VISIBLE_ITEMS);

    return (
      <div className={styles.departuresWrapper}>
        {visibleDepartures.length > 0 ? (
          <React.Fragment>
            <div className={styles.filterWrapper}>
              <Select
                className={styles.departureFilter}
                icon="Filter"
                label={t('items.modules.departureSelector.filterByAirport')}
                onChange={handleFilterDepartureSelect}
                options={filterDepartureOptions}
                value={filterDepartureCode}
              />
            </div>

            <div className={styles.departureHeader}>
              <button
                className={styles.columnHeader}
                onClick={() => handleSortClick('date')}
              >
                <Typography tagStyle="bodyMedium">
                  {t('items.modules.departureSelector.date')}
                </Typography>

                <Icon
                  className={
                    sort.key === 'date'
                      ? sort.direction === 'asc'
                        ? styles.sortAsc
                        : styles.sortDesc
                      : ''
                  }
                  name="ChevronDown"
                  size={16}
                />
              </button>

              <button className={styles.columnHeader}>
                <Typography
                  onClick={() => handleSortClick('airport')}
                  tagStyle="bodyMedium"
                >
                  {t('items.modules.departureSelector.airport')}
                </Typography>

                <Icon
                  className={
                    sort.key === 'airport'
                      ? sort.direction === 'asc'
                        ? styles.sortAsc
                        : styles.sortDesc
                      : ''
                  }
                  name="ChevronDown"
                  size={16}
                />
              </button>

              <button
                className={styles.columnHeader}
                onClick={() => handleSortClick('price')}
              >
                <Typography tagStyle="bodyMedium">
                  {t('items.modules.departureSelector.price')}
                </Typography>

                <Icon
                  className={
                    sort.key === 'price'
                      ? sort.direction === 'asc'
                        ? styles.sortAsc
                        : styles.sortDesc
                      : ''
                  }
                  name="ChevronDown"
                  size={16}
                />
              </button>

              <div className={styles.selectedIconWrapper}></div>
            </div>
          </React.Fragment>
        ) : (
          <div className={styles.noResultsWrapper}>
            <Typography
              className={styles.noResultsHeader}
              tag="label"
              tagStyle="headingMedium"
            >
              {t('items.searchWidget.states.noOffersFoundHeadline')}
            </Typography>

            <Typography
              className={styles.noResultsDescription}
              tag="span"
              tagStyle="bodyMedium"
            >
              {t('items.searchWidget.states.noOffersFoundDescription')}
            </Typography>
          </div>
        )}

        {visibleDepartures.map(departure => (
          <DepartureSelectorItem
            key={departure.transportAllotmentId}
            departureItem={departure}
            isSelected={departure.isSelected}
            onClick={() => handleDepartureClick(departure)}
            priceLabel={`${t('items.modules.configurationFlow.for')} ${getPaxInfo(
              {
                searchSettings:
                  (searchSettings as Content.Components['SearchSettingsDto']) ??
                  undefined,
                t,
              },
            )}`}
          />
        ))}

        {!showAll && departures.length > VISIBLE_ITEMS && (
          <button className={styles.link} onClick={() => setShowAll(true)}>
            {t('items.common.words.showMoreOptions', {
              options: departures.length - VISIBLE_ITEMS,
            })}
          </button>
        )}

        {showAll && departures.length > VISIBLE_ITEMS && (
          <button className={styles.link} onClick={() => setShowAll(false)}>
            {t('items.common.words.showLess')}
          </button>
        )}
      </div>
    );
  };

  if (hasNoResults) return null;

  return (
    <div className={styles.component}>
      {dataDepartureSelector?.headline && (
        <Typography className={styles.headline} tagStyle="bodyMediumBold">
          {dataDepartureSelector.headline}
        </Typography>
      )}

      {dataDepartureSelector?.description && (
        <RichText text={dataDepartureSelector?.description} />
      )}

      {renderResults()}
    </div>
  );
};

DepartureSelector.displayName = 'DepartureSelector';

export default DepartureSelector;
